import React, { useState } from 'react'
import './white-label-editor.scss'
import deepEqual from 'fast-deep-equal'
import classnames from 'classnames'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { DurationMetric } from '@pushly/aqe/lib/components/duration-input'
import { useRefState } from '@halo/hooks/use-ref-state'
import { EyeOutlined, LinkOutlined, WarningFilled } from '@ant-design/icons'
import { Col, Divider, Form, Input, InputNumber, Popover, Row, Select, Switch } from 'antd'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { useDebouncer } from '@halo/hooks/use-debouncer'
import WhiteLabelModel, { IFaviconResource } from '@halo/models/white-label.model'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { Flags } from '@pushly/aquarium-utils/lib/enums/flags'
import ColorPickerPopover from '@halo/components/color-picker-popover/color-picker-popover'
import aqe from '@pushly/aqe'

interface IWhiteLabelEditor {
    className?: string
    mode?: 'edit' | 'create'
    whiteLabel?: WhiteLabelModel
    visible?: boolean
    submitText?: string
    onSubmit?: (whiteLabel: any) => void
    onClose?: () => void
    onVisibilityChange?: (visible?: boolean) => void
}

interface IValidationResponse {
    ok: boolean
    errors: string[]
}
const setNotOk = (validation: IValidationResponse, msg: string) => {
    validation.ok = false
    validation.errors.push(msg)
}

const validateSubmit = (whiteLabel: WhiteLabelModel): IValidationResponse => {
    const res: IValidationResponse = { ok: true, errors: [] }

    if (!whiteLabel.getDisplayName()?.trim()) {
        setNotOk(res, 'Display name is required.')
    }

    if (!whiteLabel.getHostname()?.trim()) {
        setNotOk(res, 'Hostname is required.')
    }

    if (!whiteLabel.getPreviewDomainId()) {
        setNotOk(res, 'A valid Preview Domain is required.')
    }

    return res
}

const buildSubmitPackage = (propsWhiteLabel: WhiteLabelModel | undefined, workingWhiteLabel: WhiteLabelModel) => {
    const dto: any = {}

    const compareAndSetNullableString = (
        key: string,
        prev: string | null,
        next: string | null,
        emptyAsNull = false,
    ) => {
        if ((!emptyAsNull && (next ?? null) !== (prev ?? null)) || (emptyAsNull && (next || null) !== (prev || null))) {
            dto[key] = next?.trim() ?? null

            if (emptyAsNull && !dto[key]) {
                dto[key] = null
            }
        }
    }

    if (workingWhiteLabel.getIsActive() !== propsWhiteLabel?.getIsActive()) {
        dto.isActive = workingWhiteLabel.getIsActive()
    }

    const displayName = workingWhiteLabel.getDisplayName()
    if (!!displayName && displayName !== propsWhiteLabel?.getDisplayName()) {
        dto.displayName = displayName.trim()
    }

    const hostname = workingWhiteLabel.getHostname()
    if (!!hostname && hostname !== propsWhiteLabel?.getHostname()) {
        dto.hostname = hostname.trim()
    }

    const previewDomainId = workingWhiteLabel.getPreviewDomainId()
    if (!!previewDomainId && previewDomainId !== propsWhiteLabel?.getPreviewDomainId()) {
        dto.previewDomainId = previewDomainId
    }

    compareAndSetNullableString(
        'documentationLink',
        propsWhiteLabel?.getDocumentationLink(),
        workingWhiteLabel.getDocumentationLink(),
        true,
    )
    compareAndSetNullableString(
        'supportEmailAddress',
        propsWhiteLabel?.getSupportEmailAddress(),
        workingWhiteLabel.getSupportEmailAddress(),
        true,
    )

    compareAndSetNullableString('logoUrl', propsWhiteLabel?.getLogoUrl(), workingWhiteLabel.getLogoUrl(), true)
    compareAndSetNullableString(
        'primaryIconUrl',
        propsWhiteLabel?.getPrimaryIconUrl(),
        workingWhiteLabel.getPrimaryIconUrl(),
        true,
    )
    compareAndSetNullableString(
        'sidebarIconUrl',
        propsWhiteLabel?.getSidebarIconUrl(),
        workingWhiteLabel.getSidebarIconUrl(),
        true,
    )
    compareAndSetNullableString(
        'loginBackgroundUrl',
        propsWhiteLabel?.getLoginBackgroundUrl(),
        workingWhiteLabel.getLoginBackgroundUrl(),
        true,
    )

    const faviconResources = workingWhiteLabel.getFaviconResources() ?? {}
    const prevFaviconResources = propsWhiteLabel?.getFaviconResources() ?? {}
    if (!deepEqual(prevFaviconResources, faviconResources)) {
        dto.faviconResources = faviconResources
    }

    compareAndSetNullableString(
        'primaryColorHex',
        propsWhiteLabel?.getPrimaryColorHex(),
        workingWhiteLabel.getPrimaryColorHex(),
    )
    compareAndSetNullableString(
        'secondaryColorHex',
        propsWhiteLabel?.getSecondaryColorHex(),
        workingWhiteLabel.getSecondaryColorHex(),
    )
    compareAndSetNullableString(
        'tertiaryColorHex',
        propsWhiteLabel?.getTertiaryColorHex(),
        workingWhiteLabel.getTertiaryColorHex(),
    )

    if (workingWhiteLabel.getInternalIdleTimeoutMinutes() !== propsWhiteLabel?.getInternalIdleTimeoutMinutes()) {
        dto.internalIdleTimeoutMinutes = workingWhiteLabel.getInternalIdleTimeoutMinutes()
    }
    if (
        workingWhiteLabel.getInternalAbsoluteTimeoutMinutes() !==
        propsWhiteLabel?.getInternalAbsoluteTimeoutMinutes()
    ) {
        dto.internalAbsoluteTimeoutMinutes = workingWhiteLabel.getInternalAbsoluteTimeoutMinutes()
    }

    return dto
}

const MediaFormItem = (props: { label: string; value: string | null; onChange: (v: string | null) => void }) => {
    return (
        <Form.Item label={props.label}>
            <Input
                defaultValue={props.value}
                onChange={(ev) => {
                    const url = ev.target.value
                    props.onChange(url)
                }}
                suffix={
                    !!props.value ? (
                        <Popover
                            trigger="click"
                            content={
                                <img
                                    src={
                                        /^\/assets\//.test(props.value)
                                            ? `${aqe.defaults.publicPlatformDomain}${props.value}`
                                            : props.value
                                    }
                                    alt={props.label}
                                    style={{ maxHeight: 400, maxWidth: 400 }}
                                />
                            }
                        >
                            <EyeOutlined className="url-preview" />
                        </Popover>
                    ) : undefined
                }
            />
        </Form.Item>
    )
}

const DRAWER_FORM_LAYOUT = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
}

const WhiteLabelEditor = (props: IWhiteLabelEditor) => {
    const mode = props.mode ?? 'create'
    const isCreateMode = mode === 'create'

    const appState = useService<AppState>(AppState)
    const [debounce] = useDebouncer()
    const [tmpValue, setTmpValue] = useRefState<WhiteLabelModel>(props.whiteLabel?.clone() ?? WhiteLabelModel.build({}))

    const value = tmpValue.current.clone()
    const currResources = value.getFaviconResources() ?? []
    const currFaviconUrl = currResources[0]?.href
    const submitDisabled = Object.keys(buildSubmitPackage(props.whiteLabel, value)).length === 0

    const availablePreviewDomains =
        appState.currentUserDomains.filter((d) => d.flags?.includes(Flags.domain.PREVIEW_DOMAIN.key) ?? false) ?? []

    const bestMetric = (minutes: number): DurationMetric =>
        minutes >= 60 && minutes % 60 === 0 ? 'hours' : 'minutes'

    const idleMinutes = value.getInternalIdleTimeoutMinutes() ?? 720
    const absoluteMinutes = value.getInternalAbsoluteTimeoutMinutes() ?? 720

    const [idleTimeoutMetric, setIdleTimeoutMetric] = useState<DurationMetric>(() => bestMetric(idleMinutes))
    const [idleTimeoutDisplay, setIdleTimeoutDisplay] = useState<number | undefined>(() =>
        idleTimeoutMetric === 'hours' ? idleMinutes / 60 : idleMinutes,
    )
    const [absoluteTimeoutMetric, setAbsoluteTimeoutMetric] = useState<DurationMetric>(() => bestMetric(absoluteMinutes))
    const [absoluteTimeoutDisplay, setAbsoluteTimeoutDisplay] = useState<number | undefined>(() =>
        absoluteTimeoutMetric === 'hours' ? absoluteMinutes / 60 : absoluteMinutes,
    )

    return (
        <Drawer
            className={classnames('white-label-editor', `mode-${mode}`, props.className, {
                visible: props.visible,
            })}
            title={`${isCreateMode ? 'Create' : 'Edit'} White-Label`}
            width={700}
            open={props.visible ?? false}
            afterOpenChange={props.onVisibilityChange}
            disableSubmit={submitDisabled}
            submitText={props.submitText ?? (isCreateMode ? 'Submit' : 'Save')}
            onClose={() => props.onClose?.()}
            onSubmit={() => {
                const propsWhiteLabel = props.whiteLabel
                const workingWhiteLabel = tmpValue.current
                const res = validateSubmit(workingWhiteLabel.clone())

                if (res.ok) {
                    const pkg = buildSubmitPackage(propsWhiteLabel, workingWhiteLabel)
                    return props.onSubmit?.(pkg)
                } else {
                    simpleNotification(
                        'error',
                        res.errors.map((err, idx) => (
                            <div key={idx}>
                                <span>{err}</span>
                            </div>
                        )),
                        undefined,
                        undefined,
                        true,
                    )
                }
            }}
        >
            <Form
                {...DRAWER_FORM_LAYOUT}
                colon={false}
                requiredMark={false}
                labelAlign="left"
                size="small"
            >
                <Well title="General" mode="ghost" hideFooter={true} padding="none">
                    <Form.Item label="Enabled">
                        <Switch
                            size="small"
                            checked={value.getIsActive()}
                            onChange={(v) => {
                                value.setIsActive(v)
                                setTmpValue(value)
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Display Name">
                        <Input
                            defaultValue={value.getDisplayName()}
                            onChange={(ev) => {
                                const name = ev.target.value
                                debounce(() => {
                                    value.setDisplayName(name)
                                    setTmpValue(value)
                                })
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Hostname">
                        <Input
                            defaultValue={value.getHostname()}
                            onChange={(ev) => {
                                const hostname = ev.target.value
                                debounce(() => {
                                    value.setHostname(hostname)
                                    setTmpValue(value)
                                })
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Preview Domain">
                        <Select<number>
                            defaultValue={value.getPreviewDomainId()}
                            onChange={(previewDomainId) => {
                                value.setPreviewDomainId(previewDomainId)
                                setTmpValue(value)
                            }}
                        >
                            {availablePreviewDomains.map((d) => (
                                <Select.Option key={d.id} value={d.id}>
                                    {d.display_name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                Documentation Link
                                {!!value.getDocumentationLink() && (
                                    <span className="url-preview" onClick={() => window.open(value.getDocumentationLink())}>
                                        <LinkOutlined />
                                    </span>
                                )}
                            </span>
                        }
                    >
                        <Input
                            defaultValue={value.getDocumentationLink()}
                            onChange={(ev) => {
                                const url = ev.target.value
                                debounce(() => {
                                    value.setDocumentationLink(url)
                                    setTmpValue(value)
                                })
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Support Email">
                        <Input
                            defaultValue={value.getSupportEmailAddress()}
                            onChange={(ev) => {
                                const addr = ev.target.value
                                debounce(() => {
                                    value.setSupportEmailAddress(addr)
                                    setTmpValue(value)
                                })
                            }}
                        />
                    </Form.Item>
                </Well>

                <Divider className="inner-well-divider" />

                <Well title="Media" mode="ghost" hideFooter={true} padding="none">
                    <MediaFormItem
                        label="Logo URL"
                        value={value.getLogoUrl()}
                        onChange={(url) => {
                            debounce(() => {
                                value.setLogoUrl(url)
                                setTmpValue(value)
                            })
                        }}
                    />

                    <MediaFormItem
                        label="Primary Icon"
                        value={value.getPrimaryIconUrl()}
                        onChange={(url) => {
                            debounce(() => {
                                value.setPrimaryIconUrl(url)
                                setTmpValue(value)
                            })
                        }}
                    />

                    <MediaFormItem
                        label="Sidebar Icon"
                        value={value.getSidebarIconUrl()}
                        onChange={(url) => {
                            debounce(() => {
                                value.setSidebarIconUrl(url)
                                setTmpValue(value)
                            })
                        }}
                    />

                    <MediaFormItem
                        label="Favicon"
                        value={currFaviconUrl}
                        onChange={(href) => {
                            debounce(() => {
                                const favicon: IFaviconResource = {
                                    rel: 'shortcut icon',
                                    href,
                                }

                                value.setFaviconResources(!href ? null : [favicon])
                                setTmpValue(value)
                            })
                        }}
                    />

                    <MediaFormItem
                        label="Login Background"
                        value={value.getLoginBackgroundUrl()}
                        onChange={(url) => {
                            debounce(() => {
                                value.setLoginBackgroundUrl(url)
                                setTmpValue(value)
                            })
                        }}
                    />
                </Well>

                <Divider className="inner-well-divider" />

                <Well title="Branding" mode="ghost" hideFooter={true} padding="none">
                    <Row gutter={24}>
                        <Col span={8}>
                            <Form.Item label="Primary" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                                <ColorPickerPopover
                                    defaultColor={props.whiteLabel?.getPrimaryColorHex()}
                                    color={value.getPrimaryColorHex()}
                                    onChange={(hex) => {
                                        value.setPrimaryColorHex(hex)
                                        setTmpValue(value)
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Secondary" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                                <ColorPickerPopover
                                    defaultColor={props.whiteLabel?.getSecondaryColorHex()}
                                    color={value.getSecondaryColorHex()}
                                    onChange={(hex) => {
                                        value.setSecondaryColorHex(hex)
                                        setTmpValue(value)
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Tertiary" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                                <ColorPickerPopover
                                    defaultColor={props.whiteLabel?.getTertiaryColorHex()}
                                    color={value.getTertiaryColorHex()}
                                    onChange={(hex) => {
                                        value.setTertiaryColorHex(hex)
                                        setTmpValue(value)
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Well>

                <Divider className="inner-well-divider" />

                <Well title="Session Timeouts" mode="ghost" hideFooter={true} padding="none">
                    <Form.Item label="Idle Timeout">
                        <Input.Group compact>
                            <InputNumber
                                value={idleTimeoutDisplay}
                                min={idleTimeoutMetric === 'hours' ? 1 : 15}
                                max={idleTimeoutMetric === 'hours' ? 24 : 1440}
                                onChange={(v: number | null) => {
                                    const val = v ?? undefined
                                    if (val !== undefined) {
                                        const minutes = idleTimeoutMetric === 'hours' ? val * 60 : val
                                        value.setInternalIdleTimeoutMinutes(minutes)
                                        setTmpValue(value)
                                    }
                                    setIdleTimeoutDisplay(val)
                                }}
                                style={{ width: 80 }}
                            />
                            <Select
                                value={idleTimeoutMetric}
                                onChange={(m: DurationMetric) => {
                                    setIdleTimeoutMetric(m)
                                    // Reinterpret the current display value with the new metric
                                    if (idleTimeoutDisplay !== undefined) {
                                        const minutes = m === 'hours' ? idleTimeoutDisplay * 60 : idleTimeoutDisplay
                                        value.setInternalIdleTimeoutMinutes(minutes)
                                        setTmpValue(value)
                                    }
                                }}
                                style={{ width: 120 }}
                            >
                                <Select.Option value="minutes">minute(s)</Select.Option>
                                <Select.Option value="hours">hour(s)</Select.Option>
                            </Select>
                        </Input.Group>
                    </Form.Item>

                    <Form.Item label="Absolute Timeout">
                        <Input.Group compact>
                            <InputNumber
                                value={absoluteTimeoutDisplay}
                                min={absoluteTimeoutMetric === 'hours' ? 1 : 60}
                                max={absoluteTimeoutMetric === 'hours' ? 24 : 1440}
                                onChange={(v: number | null) => {
                                    const val = v ?? undefined
                                    if (val !== undefined) {
                                        const minutes = absoluteTimeoutMetric === 'hours' ? val * 60 : val
                                        value.setInternalAbsoluteTimeoutMinutes(minutes)
                                        setTmpValue(value)
                                    }
                                    setAbsoluteTimeoutDisplay(val)
                                }}
                                style={{ width: 80 }}
                            />
                            <Select
                                value={absoluteTimeoutMetric}
                                onChange={(m: DurationMetric) => {
                                    setAbsoluteTimeoutMetric(m)
                                    // Reinterpret the current display value with the new metric
                                    if (absoluteTimeoutDisplay !== undefined) {
                                        const minutes = m === 'hours' ? absoluteTimeoutDisplay * 60 : absoluteTimeoutDisplay
                                        value.setInternalAbsoluteTimeoutMinutes(minutes)
                                        setTmpValue(value)
                                    }
                                }}
                                style={{ width: 120 }}
                            >
                                <Select.Option value="minutes">minute(s)</Select.Option>
                                <Select.Option value="hours">hour(s)</Select.Option>
                            </Select>
                        </Input.Group>
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

export default WhiteLabelEditor
